import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SUPPORTED_LANGS = ["en", "hi", "kn", "ta", "ml", "te", "bn", "mr", "gu", "other"];
const empty = { slug: "", icon: "sparkles", image_url: "", video_url: "", sort: 0, active: true, labels: {}, tagline: {} };

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => setItems((await api.get("/admin/categories")).data);
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/admin/categories", form);
      else await api.patch(`/admin/categories/${editing}`, form);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/categories/${id}`); load();
  };

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Catalogue</p>
          <h1 className="editorial-h text-5xl text-brand-dark mt-2">Categories</h1>
        </div>
        <Button onClick={() => { setEditing("new"); setForm(empty); }} data-testid="category-new-btn" className="rounded-none bg-brand-primary text-brand-bone gap-2">
          <Plus strokeWidth={1.5} className="w-4 h-4" /> Add category
        </Button>
      </div>

      {editing && (
        <form onSubmit={save} className="mt-8 bg-white border border-brand-line p-6 space-y-4" data-testid="category-form">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="overline">Slug</Label>
              <Input className="mt-2 h-11 rounded-none border-brand-line" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required />
            </div>
            <div>
              <Label className="overline">Icon (sparkles / wind / droplet)</Label>
              <Input className="mt-2 h-11 rounded-none border-brand-line" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} />
            </div>
            <div>
              <Label className="overline">Sort</Label>
              <Input type="number" className="mt-2 h-11 rounded-none border-brand-line" value={form.sort} onChange={(e) => setForm({ ...form, sort: Number(e.target.value) })} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="overline">Hero image URL (concern card)</Label>
              <Input className="mt-2 h-11 rounded-none border-brand-line" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." data-testid="category-form-image" />
              {form.image_url && <img src={form.image_url} alt="" className="mt-2 w-full max-w-xs aspect-video object-cover bg-brand-elevated" />}
            </div>
            <div>
              <Label className="overline">Video banner URL (top of recommendations)</Label>
              <Input className="mt-2 h-11 rounded-none border-brand-line" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://.../video.mp4" data-testid="category-form-video" />
              <p className="text-xs text-brand-muted mt-1">Direct MP4 / WebM URL. Will autoplay muted on the customer recommendations page.</p>
            </div>
          </div>
          <div>
            <Label className="overline">Labels (per language)</Label>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
              {SUPPORTED_LANGS.map((l) => (
                <div key={l}>
                  <p className="text-[10px] uppercase tracking-widest text-brand-muted">{l}</p>
                  <Input className="h-10 rounded-none border-brand-line" value={form.labels[l] || ""} onChange={(e) => setForm({ ...form, labels: { ...form.labels, [l]: e.target.value } })} required={l === "en"} />
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="overline">Tagline / examples (per language)</Label>
            <p className="text-xs text-brand-muted mt-1 mb-2">Shown below the title on the concern card. E.g. &ldquo;Dark skin &middot; Tanning &middot; Pigmentation&rdquo;</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {SUPPORTED_LANGS.map((l) => (
                <div key={l}>
                  <p className="text-[10px] uppercase tracking-widest text-brand-muted">{l}</p>
                  <Input className="h-10 rounded-none border-brand-line" value={form.tagline?.[l] || ""} onChange={(e) => setForm({ ...form, tagline: { ...(form.tagline || {}), [l]: e.target.value } })} />
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            <Label className="font-sans text-sm">Active</Label>
          </div>
          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-none border-brand-line">Cancel</Button>
            <Button type="submit" className="rounded-none bg-brand-primary text-brand-bone">Save</Button>
          </div>
        </form>
      )}

      <div className="mt-8 grid sm:grid-cols-2 gap-3">
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-brand-line p-4 flex justify-between items-center" data-testid={`category-row-${c.slug}`}>
            <div>
              <p className="font-serif text-2xl text-brand-dark">{c.labels?.en || c.slug}</p>
              <p className="text-xs text-brand-muted mt-1">slug: {c.slug} · icon: {c.icon}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setEditing(c.id); setForm({ slug: c.slug, icon: c.icon, image_url: c.image_url || "", video_url: c.video_url || "", sort: c.sort || 0, active: c.active, labels: c.labels || {}, tagline: c.tagline || {} }); }} className="text-brand-dark hover:text-brand-primary"><Pencil strokeWidth={1.5} className="w-4 h-4" /></button>
              <button onClick={() => remove(c.id)} className="text-brand-dark hover:text-brand-primary"><Trash2 strokeWidth={1.5} className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
